import { Fragment, useRef, useState, useSyncExternalStore, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Camera, ChevronDown, Ellipsis, Images, Link2, Redo2, SquareMousePointer, Undo2, type LucideIcon } from 'lucide-react';
import { Sheet } from '../../ui/Dialog';
import { FLOWCHART_TEMPLATES, SHAPES, type FlowchartLabels } from '@os/doc';
import { KEYMAP, type ToolId } from '@os/engine';
import { READ_ONLY_TOOLS, type EditorController } from './controller';
import { LinkInput } from '../links/LinkInput';
import { cn, IconButton, Menu, ToolTile } from '../../ui';
import { Icon, TOOL_ICONS } from '../../ui/icons';
import { ToolArt, type ToolArtKind } from '../../ui/tool-art/ToolArt';
import { useLayout } from '../../app/layout';
import { FLOATING_BAR } from './canvas-ui';

export const TOOLS: readonly ToolId[] = [
  'select',
  'hand',
  'text',
  'sticky',
  'shape',
  'arrow',
  'connector',
  'section',
  'frame',
  'table',
  'tasklist',
  'image',
];

/** Tools of the "+" panel on desktop and tablet (Ruling 6); the bar keeps the others. */
export const PLUS_TOOLS: readonly ToolId[] = ['arrow', 'frame', 'tasklist', 'image'];
/** Creation tools drawn as illustrations (backlog §10.1). */
const ART_TOOLS: Partial<Record<ToolId, ToolArtKind>> = { sticky: 'sticky', shape: 'shape', connector: 'connector', text: 'text', section: 'section', table: 'table', tasklist: 'tasklist', comment: 'comment' };

/** First single-key shortcut of each tool, read from the shared KEYMAP. */
const KEY_OF = Object.fromEntries(
  Object.entries(KEYMAP)
    .filter(
      ([combo, action]) =>
        (combo.length === 1 || /^shift\+.$/.test(combo)) && [...TOOLS, 'comment'].includes(action as ToolId),
    )
    .reverse()
    .map(([combo, action]) => [action, combo.replace('shift+', 'Shift+').toUpperCase()]),
) as Partial<Record<ToolId, string>>;

const Glyph = ({ icon }: { icon: LucideIcon }) => <Icon icon={icon} />;

/** Toolbar button with the toolbar's look: neutral, a full accent pill when pressed; creation tools are illustrations. */
function BarButton(props: { label: string; icon: LucideIcon; art?: ToolArtKind; shortcut?: string; pressed?: boolean; onClick: () => void; expanded?: boolean }) {
  return (
    <IconButton
      label={props.label}
      shortcut={props.shortcut}
      pressed={props.pressed}
      aria-expanded={props.expanded}
      onClick={props.onClick}
      icon={props.art ? <ToolArt kind={props.art} /> : <Glyph icon={props.icon} />}
      className={cn(
        'rounded-full os-press os-tool',
        props.art ? 'h-11 w-11 text-fg' : 'h-10 w-10',
      )}
    />
  );
}

const menuTrigger =
  'inline-flex h-10 shrink-0 items-center justify-center rounded-full text-fg-muted os-press hover:bg-chrome-hover hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus data-[popup-open]:bg-surface-2 data-[popup-open]:text-fg pointer-coarse:h-11';

const Separator = ({ vertical }: { vertical: boolean }) =>
  vertical ? (
    <span role="separator" aria-orientation="horizontal" className="mx-1.5 my-1 h-px shrink-0 self-stretch bg-border" />
  ) : (
    <span role="separator" aria-orientation="vertical" className="mx-1 my-1.5 w-px shrink-0 self-stretch bg-border" />
  );

/** Tools on the phone bar (P5f, backlog §14.4); the others go in its "+" sheet. */
const PHONE_TOOLS: readonly ToolId[] = ['sticky', 'shape', 'text'];
/** Tools of the phone's "+" sheet, before the image, link, shape, template and multi-select tiles. */
const PHONE_SHEET_TOOLS: readonly ToolId[] = ['connector', 'arrow', 'section', 'frame', 'table', 'tasklist'];
const BAR_CLASS = {
  desktop: 'bottom-4 left-1/2 -translate-x-1/2 items-center rounded-full',
  // Left side, clear of the 16 px system edge; scrolls when a landscape tablet is too short for every tool.
  tablet: 'left-[max(1rem,env(safe-area-inset-left))] top-1/2 max-h-[calc(100dvh-8rem)] -translate-y-1/2 flex-col items-center overflow-y-auto rounded-3xl',
  // Fixed 44 px buttons: the properties sheet rests on this bar's height. Side padding keeps them out of the 16 px
  // system edge (P15 audit: the first and last sat at 12 px).
  phone: 'inset-x-0 bottom-0 items-center justify-around rounded-none *:h-11 *:w-11 pb-[max(0.375rem,env(safe-area-inset-bottom))] pl-[max(1rem,env(safe-area-inset-left))] pr-[max(1rem,env(safe-area-inset-right))]',
} as const;

/**
 * Floating toolbar, in four groups (navigate | draw | place | board): bottom centre on desktop, a vertical bar on the
 * left on tablets, and on phones a bottom bar with four tools, "+" (a sheet with the rest), undo and redo (P5f); the
 * phone's palette, settings, export and help live in the main menu.
 * Escape returns to the canvas, except inside one of its menus, which Escape closes first.
 */
export function Toolbar({
  controller,
  onPickImage,
  onTakePhoto,
}: {
  controller: EditorController;
  /** Touch layouts: the image tool offers the library and the camera; each must open its picker inside the tap. */
  onPickImage?: () => void;
  onTakePhoto?: () => void;
}) {
  const { t } = useTranslation();
  const ui = useSyncExternalStore(controller.subscribeUi, controller.ui);
  const layout = useLayout();
  const vertical = layout === 'tablet';
  const popupSide = vertical ? 'right' : 'top';
  const [linking, setLinking] = useState(false);
  const [sheet, setSheet] = useState(false);
  // Actions that open something focusable run once the More menu has closed and given the focus back.
  const afterMenu = useRef<(() => void) | null>(null);
  // Set by those actions: the closing menu must not hand the focus back to its trigger over what they open.
  const handingOff = useRef(false);
  const labels = () =>
    Object.fromEntries(
      (['start', 'step1', 'step2', 'end', 'choice', 'yes', 'no'] as const).map((k) => [
        k,
        t(`templates.${k}`),
      ]),
    ) as FlowchartLabels;
  const allowed = (tool: ToolId) => !ui.readOnly || READ_ONLY_TOOLS.has(tool);
  const toolButton = (tool: ToolId) => (
    <BarButton key={tool} label={t(`tools.${tool}`)} icon={TOOL_ICONS[tool]} art={ART_TOOLS[tool]} shortcut={KEY_OF[tool]} pressed={ui.tool === tool} onClick={() => controller.pickTool(tool)} />
  );
  const shapeMenu = (
    <Menu.Root key="shape-options">
      <Menu.Trigger aria-label={t('toolbar.shapeOptions')} className={cn(menuTrigger, '-ml-1 w-5 max-sm:ml-0 max-sm:w-10 pointer-coarse:w-11')}>
        <ChevronDown size={14} strokeWidth={2} aria-hidden="true" />
      </Menu.Trigger>
      <Menu.Popup label={t('toolbar.shapeOptions')} side={popupSide} sideOffset={10}>
        {SHAPES.map((kind) => (
          <Menu.Item
            key={kind}
            onClick={() => {
              controller.setShapeKind(kind);
              controller.focus();
            }}
          >
            {t(`shapes.${kind}`)}
          </Menu.Item>
        ))}
      </Menu.Popup>
    </Menu.Root>
  );
  const multiSelect = (
    <BarButton key="multi" label={t('toolbar.multiSelect')} icon={SquareMousePointer} pressed={ui.multiSelect} onClick={() => controller.setMultiSelect(!ui.multiSelect)} />
  );
  const later = (run: () => void) => () => {
    afterMenu.current = run;
    handingOff.current = true;
  };
  const moreItem = (key: string, label: string, onClick: () => void) => (
    <Menu.Item key={key} onClick={onClick}>
      {label}
    </Menu.Item>
  );
  // Shared by the More (phone) and "+" menus: only one is ever open.
  const handOff = {
    onOpenChange: (open: boolean) => {
      if (open) handingOff.current = false;
    },
    onOpenChangeComplete: (open: boolean) => {
      const run = afterMenu.current;
      afterMenu.current = null;
      if (!open) run?.();
    },
  };
  const templateItems = FLOWCHART_TEMPLATES.map((kind) =>
    moreItem(kind, t(`templates.${kind}`), () => {
      controller.insertTemplate(kind, labels());
      controller.focus();
    }),
  );
  const pick = (tool: ToolId) => () => {
    controller.pickTool(tool);
    controller.focus();
  };
  // Desktop and tablet: everything the four groups leave out (Ruling 6).
  const plusMenu = (
    <Menu.Root key="plus" {...handOff}>
      <Menu.Trigger
        aria-label={t('toolbar.moreTools')}
        data-active-tool={PLUS_TOOLS.includes(ui.tool) ? ui.tool : undefined}
        className={cn(menuTrigger, 'h-11 w-11 rounded-full text-fg data-[active-tool]:bg-accent-soft data-[active-tool]:text-fg')}
      >
        <ToolArt kind="plus" />
      </Menu.Trigger>
      <Menu.Popup label={t('toolbar.moreTools')} side={popupSide} sideOffset={10} finalFocus={() => !handingOff.current}>
        {moreItem('arrow', t('tools.arrow'), pick('arrow'))}
        {moreItem('frame', t('tools.frame'), pick('frame'))}
        {!ui.readOnly && moreItem('tasklist', t('tools.tasklist'), pick('tasklist'))}
        {layout === 'desktop' ? (
          moreItem('image', t('tools.image'), pick('image'))
        ) : (
          // Touch: each picker must open inside the tap.
          <Menu.SubmenuRoot>
            <Menu.SubmenuTrigger>{t('tools.image')}</Menu.SubmenuTrigger>
            <Menu.Popup label={t('tools.image')} side={popupSide} sideOffset={4}>
              {moreItem('camera', t('toolbar.camera'), () => onTakePhoto?.())}
              {moreItem('library', t('toolbar.library'), () => onPickImage?.())}
            </Menu.Popup>
          </Menu.SubmenuRoot>
        )}
        {moreItem('link', t('links.add'), later(() => setLinking(true)))}
        <Menu.SubmenuRoot>
          <Menu.SubmenuTrigger>{t('templates.label')}</Menu.SubmenuTrigger>
          <Menu.Popup label={t('templates.label')} side={popupSide} sideOffset={4}>
            {templateItems}
          </Menu.Popup>
        </Menu.SubmenuRoot>
      </Menu.Popup>
    </Menu.Root>
  );
  // Phones: one button flips between hand (the view follows the finger, the default) and select (objects are picked up).
  const modeButton = (
    <BarButton
      key="mode"
      label={t(ui.tool === 'select' ? 'toolbar.modeSelect' : 'toolbar.modeHand')}
      icon={TOOL_ICONS[ui.tool === 'select' ? 'select' : 'hand']}
      pressed={ui.tool === 'select' || ui.tool === 'hand'}
      onClick={() => controller.pickTool(ui.tool === 'hand' ? 'select' : 'hand')}
    />
  );
  // Phones (P5f): the mode button, three tools, "+", undo and redo; a reader gets select, hand (and comment).
  const phoneBar = ui.readOnly
    ? [toolButton('select'), toolButton('hand'), ...(ui.canComment ? [toolButton('comment')] : [])]
    : [
        modeButton,
        ...PHONE_TOOLS.map(toolButton),
        <BarButton key="plus" label={t('toolbar.moreTools')} icon={Ellipsis} art="plus" expanded={sheet} pressed={PHONE_SHEET_TOOLS.includes(ui.tool) || undefined} onClick={() => setSheet(true)} />,
        <BarButton key="undo" label={t('commands.undo')} icon={Undo2} onClick={() => controller.runCommand('undo')} />,
        <BarButton key="redo" label={t('commands.redo')} icon={Redo2} onClick={() => controller.runCommand('redo')} />,
      ];
  const closeSheet = (then?: () => void) => {
    setSheet(false);
    if (then) setTimeout(then, 0); // after the sheet hands the focus back
  };
  const sheetTile = (key: string, label: string, art: ReactNode, onClick: () => void, pressed?: boolean) => (
    <ToolTile key={key} label={label} art={art} pressed={pressed} onClick={onClick} />
  );
  const toolArt = (tool: ToolId) => (ART_TOOLS[tool] ? <ToolArt kind={ART_TOOLS[tool]!} /> : <Glyph icon={TOOL_ICONS[tool]} />);
  const plusSheet = sheet && (
    <Sheet modal title={t('toolbar.moreTools')} closeLabel={t('common.close')} onClose={() => closeSheet()}>
      <div className="grid grid-cols-4 gap-2 pb-2">
        {PHONE_SHEET_TOOLS.map((tool) =>
          sheetTile(tool, t(`tools.${tool}`), toolArt(tool), () => {
            controller.pickTool(tool);
            closeSheet();
          }, ui.tool === tool),
        )}
        {/* Each picker opens inside the tap (a browser needs the user's gesture). */}
        {sheetTile('camera', t('toolbar.camera'), <Glyph icon={Camera} />, () => {
          onTakePhoto?.();
          closeSheet();
        })}
        {sheetTile('library', t('toolbar.library'), <Glyph icon={Images} />, () => {
          onPickImage?.();
          closeSheet();
        })}
        {sheetTile('link', t('links.add'), <Glyph icon={Link2} />, () => closeSheet(() => setLinking(true)))}
        {ui.canComment && sheetTile('comment', t('tools.comment'), toolArt('comment'), () => {
          controller.setTool('comment');
          closeSheet();
        })}
        {sheetTile('multi', t('toolbar.multiSelect'), <Glyph icon={SquareMousePointer} />, () => controller.setMultiSelect(!ui.multiSelect), ui.multiSelect)}
      </div>
      <p className="mb-1.5 mt-2 text-xs font-medium text-fg-muted">{t('toolbar.shapeOptions')}</p>
      <div className="grid grid-cols-4 gap-2 pb-2">
        {SHAPES.map((kind) =>
          sheetTile(`shape-${kind}`, t(`shapes.${kind}`), <ToolArt kind="shape" />, () => {
            controller.setShapeKind(kind);
            controller.setTool('shape');
            closeSheet();
          }),
        )}
      </div>
      <p className="mb-1.5 mt-2 text-xs font-medium text-fg-muted">{t('templates.label')}</p>
      <div className="grid grid-cols-4 gap-2 pb-2">
        {FLOWCHART_TEMPLATES.map((kind) =>
          sheetTile(`template-${kind}`, t(`templates.${kind}`), <ToolArt kind="connector" />, () => {
            controller.insertTemplate(kind, labels());
            closeSheet();
          }),
        )}
      </div>
    </Sheet>
  );

  const groups: ReactNode[][] =
    layout === 'phone'
      ? [phoneBar]
      : [
    [...(['select', 'hand'] as const).filter(allowed).map(toolButton), ...(vertical ? [multiSelect] : [])],
    ui.readOnly ? [] : [toolButton('sticky'), toolButton('shape'), shapeMenu, toolButton('connector')],
    [...(ui.readOnly ? [] : [toolButton('text'), toolButton('section'), toolButton('table')]), ...(ui.canComment ? [toolButton('comment')] : [])],
    ui.readOnly
      ? []
      : [
          // A touch tablet has no keyboard for undo: its bar keeps the two buttons (P5d).
          ...(vertical
            ? [
                <BarButton key="undo" label={t('commands.undo')} icon={Undo2} onClick={() => controller.runCommand('undo')} />,
                <BarButton key="redo" label={t('commands.redo')} icon={Redo2} onClick={() => controller.runCommand('redo')} />,
              ]
            : []),
          plusMenu,
        ],
  ].filter((g) => g.length > 0);

  // Popovers sit outside the bar: on phones the bar scrolls sideways and would clip them.
  return (
    <>
      <div
        role="toolbar"
        data-fit-inset=""
        aria-label={t('toolbar.label')}
        aria-orientation={vertical ? 'vertical' : undefined}
        data-layout={layout}
        onKeyDown={(e) => {
          if (e.key !== 'Escape' || (e.target instanceof Element && e.target.closest('[role="menu"]'))) return;
          controller.setTool('select');
          controller.focus();
        }}
        className={cn('absolute z-10 flex gap-0.5 p-1.5', FLOATING_BAR, BAR_CLASS[layout])}
      >
        {groups.map((group, i) => (
          <Fragment key={i}>
            {i > 0 && <Separator vertical={vertical} />}
            {group}
          </Fragment>
        ))}
      </div>
      {plusSheet}
      {linking && (
        <LinkInput
          onCreate={(url) => controller.createLink(url)}
          onClose={() => {
            setLinking(false);
            controller.focus();
          }}
        />
      )}
    </>
  );
}
